import {
  AccountTree as AccountTreeIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  Healing as HealingIcon,
  HistoryToggleOff as HistoryToggleOffIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
  SsidChart as SsidChartIcon,
} from "@mui/icons-material";
import { IconButton, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import PageHeader from "../../../components/page-header";
import { TagList } from "../../../components/tag-list";
import type { Test, TestContext } from "../../../types";
import { clickableProps } from "../../../utils/keyboard";

interface TestDetailsHeaderProps {
  test: Test | undefined;
  isEditingTestName: boolean;
  editedTestName: string;
  setEditedTestName: (name: string) => void;
  startEditingTestName: (test: Test) => void;
  cancelEditingTestName: () => void;
  saveTestName: (test: Test) => void;
  isUpdatingTest: boolean;
  isDuplicating: boolean;
  isMutating: boolean;
  hasEnabledSteps: boolean;
  ctxs: TestContext[] | undefined;
  onRunTest: () => void;
  onRunTestWithFix: () => void;
  onDuplicateTest: () => void;
  onViewVersions: () => void;
  onViewEdgeCases: () => void;
  onViewOriginTest: (testId: string) => void;
  onToggleExpectedBehavior?: () => void;
}

export function TestDetailsHeader({
  test,
  isEditingTestName,
  editedTestName,
  setEditedTestName,
  startEditingTestName,
  cancelEditingTestName,
  saveTestName,
  isUpdatingTest,
  isDuplicating,
  isMutating,
  hasEnabledSteps,
  ctxs,
  onRunTest,
  onRunTestWithFix,
  onDuplicateTest,
  onViewVersions,
  onViewEdgeCases,
  onViewOriginTest,
  onToggleExpectedBehavior,
}: TestDetailsHeaderProps) {
  const title = isEditingTestName ? (
    <Stack direction="row" spacing={1} alignItems="center" maxWidth="750px">
      <TextField
        autoFocus
        size="small"
        value={editedTestName}
        onChange={(e) => setEditedTestName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            saveTestName(test!);
          } else if (e.key === "Escape") {
            cancelEditingTestName();
          }
        }}
        sx={{ minWidth: "300px" }}
      />
      <Tooltip title="Save test name">
        <IconButton
          color="primary"
          onClick={() => saveTestName(test!)}
          disabled={isUpdatingTest}
          size="small"
          aria-label="Save test name"
        >
          <SaveIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel editing">
        <IconButton
          color="default"
          onClick={cancelEditingTestName}
          size="small"
          aria-label="Cancel editing"
        >
          <CancelIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  ) : (
    <Stack direction="row" spacing={2} alignItems="center">
      <Tooltip title={`Click to edit test name: ${test?.name}`}>
        <Typography
          variant="h4"
          maxWidth="750px"
          sx={{
            cursor: "pointer",
            fontWeight: 600,
            color: "text.primary",
            letterSpacing: "-0.02em",
            "&:hover": {
              textDecoration: "underline",
              color: "primary.main",
            },
          }}
          {...clickableProps(() => test && startEditingTestName(test))}
          onClick={() => test && startEditingTestName(test)}
          noWrap
        >
          {test?.name ?? "No name"}
        </Typography>
      </Tooltip>
      {test?.version_hash && (
        <Tooltip title={test.version_hash}>
          <Typography variant="subtitle1" sx={{ color: "text.secondary" }}>
            <code>{test.version_hash.slice(0, 8)}</code>
          </Typography>
        </Tooltip>
      )}
      {test?.tags && test.tags.length > 0 && (
        <TagList tags={test.tags} size="small" variant="outlined" />
      )}
    </Stack>
  );

  const actions = (
    <>
      {test?.test_variant_for && (
        <Tooltip
          title="This test is a variant of another test, click to see origin test"
          placement="top"
        >
          <IconButton
            size="small"
            onClick={() => onViewOriginTest(test.test_variant_for!)}
            aria-label="View Origin Test"
            sx={{
              borderRadius: 2,
              p: 1.5,
              border: "0px solid",
              borderColor: "grey.300",
              color: "grey.800",
              backgroundColor: "transparent",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "common.white",
              },
            }}
          >
            <SsidChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Duplicate this test" placement="top">
        <IconButton
          size="small"
          onClick={onDuplicateTest}
          disabled={isDuplicating}
          aria-label="Duplicate test"
          sx={{
            borderRadius: 2,
            p: 1.5,
            border: "0px solid",
            borderColor: "grey.300",
            color: "grey.800",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "common.white",
            },
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="See previous versions" placement="top">
        <IconButton
          size="small"
          onClick={onViewVersions}
          aria-label="Versions"
          sx={{
            borderRadius: 2,
            p: 1.5,
            border: "0px solid",
            borderColor: "grey.300",
            color: "grey.800",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "common.white",
            },
          }}
        >
          <HistoryToggleOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          !hasEnabledSteps
            ? "No enabled steps - add and enable test steps first"
            : "Find new test edge cases"
        }
        placement="top"
      >
        <IconButton
          size="small"
          onClick={onViewEdgeCases}
          disabled={!hasEnabledSteps}
          aria-label="Edge Cases"
          sx={{
            borderRadius: 2,
            p: 1.5,
            border: "0px solid",
            borderColor: "grey.300",
            color: "grey.800",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "common.white",
            },
          }}
        >
          <AccountTreeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          !hasEnabledSteps
            ? "No enabled steps - add and enable test steps first"
            : "Trigger an auto-fixing run"
        }
        placement="top"
      >
        <IconButton
          size="small"
          onClick={onRunTestWithFix}
          disabled={isMutating || !hasEnabledSteps}
          aria-label="Run test"
          sx={{
            borderRadius: 2,
            p: 1.5,
            border: "0px solid",
            borderColor: "grey.300",
            color: "grey.800",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "common.white",
            },
          }}
        >
          <HealingIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          !hasEnabledSteps
            ? "No enabled steps - add and enable test steps first"
            : "Trigger a new run"
        }
        placement="top"
      >
        <IconButton
          size="small"
          onClick={onRunTest}
          disabled={isMutating || !hasEnabledSteps}
          aria-label="Run test"
          sx={{
            borderRadius: 2,
            p: 1.5,
            border: "0px solid",
            borderColor: "grey.300",
            color: "grey.800",
            backgroundColor: "transparent",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "common.white",
            },
          }}
        >
          <PlayArrowIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  return (
    <PageHeader title={title} actions={actions}>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        divider={<Typography variant="body2">·</Typography>}
      >
        <Typography variant="body2">{new Date(test?.created_at ?? 0).toLocaleString()}</Typography>
        <Typography variant="body2">{ctxs?.length ?? 0} steps</Typography>
        <Typography variant="body2">
          {(test?.test_dependency_ids ?? []).length > 1
            ? `${test?.test_dependency_ids?.length} dependencies`
            : "No dependencies"}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2">
            {test?.is_positive === false ? "Expected to fail" : "Expected to pass"}
          </Typography>
          {onToggleExpectedBehavior && (
            <Tooltip
              title={
                test?.is_positive === false
                  ? "Change to expected to pass"
                  : "Change to expected to fail"
              }
            >
              <Switch
                size="small"
                checked={test?.is_positive !== false}
                onChange={() => {
                  if (onToggleExpectedBehavior) {
                    onToggleExpectedBehavior();
                  }
                }}
                disabled={isUpdatingTest}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-disabled": {
                    "& + .MuiSwitch-track": {
                      opacity: 0.3,
                    },
                  },
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {test?.success_criteria && (
        <Tooltip title={`${test.is_positive ? "Success" : "Failure"} criteria`} placement="top">
          <Typography variant="caption" color="text.secondary">
            {test.success_criteria}
          </Typography>
        </Tooltip>
      )}
    </PageHeader>
  );
}
